import React, { useState, useEffect } from 'react';

const RoleModal = ({ isOpen, setIsOpen, onCreate, availablePermissions, initialData = null, title }) => {
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (initialData) {
      setRoleName(initialData.name || '');
      const permIds = initialData.permissions 
        ? initialData.permissions.map(p => p.id || p)
        : [];
      setSelectedPermissions(permIds);
    } else {
      setRoleName('');
      setSelectedPermissions([]);
    }
    setError('');
    setSearchTerm('');
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (roleName.trim() && selectedPermissions.length > 0) {
      setLoading(true);
      setError('');
      
      const roleData = {
        name: roleName.trim(),
        permissions: selectedPermissions,
      };
      
      if (initialData?.id) {
        roleData.id = initialData.id;
      }
      
      try {
        await onCreate(roleData);
        setIsOpen(false);
        setRoleName('');
        setSelectedPermissions([]);
      } catch (err) {
        setError(err.message || "Erreur lors de l'enregistrement");
      } finally {
        setLoading(false);
      }
    }
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const filteredPermissions = availablePermissions.filter(perm =>
    perm.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all duration-300"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[#5e17eb] to-[#7c3aed] px-6 py-4 rounded-t-2xl sticky top-0">
          <h2 className="text-2xl font-bold text-white text-center">{title}</h2>
        </div>
        
        <form className="p-6" onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}
          
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nom du Rôle
            </label>
            <input
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#5e17eb] focus:ring-2 focus:ring-[#5e17eb]/30 transition-all duration-200"
              placeholder="Ex: Super Admin, Manager, Employé..."
              disabled={loading}
              autoFocus
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Permissions ({selectedPermissions.length} sélectionnée(s))
            </label>
            
            {/* Search bar */}
            <div className="mb-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher une permission..."
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#5e17eb] text-sm"
              />
            </div>
            
            {/* Permissions list */}
            <div className="border-2 border-gray-200 rounded-xl max-h-60 overflow-y-auto p-3 bg-gray-50">
              {filteredPermissions.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  {availablePermissions.length === 0 
                    ? "Aucune permission disponible. Veuillez d'abord créer des permissions."
                    : "Aucune permission trouvée"}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {filteredPermissions.map((permission) => (
                    <label
                      key={permission.id}
                      className="flex items-center space-x-2 p-2 hover:bg-white rounded-lg cursor-pointer transition-all duration-200"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.id)}
                        onChange={() => togglePermission(permission.id)}
                        className="w-4 h-4 text-[#5e17eb] focus:ring-[#5e17eb] border-gray-300 rounded cursor-pointer"
                        disabled={loading}
                      />
                      <span className="text-sm text-gray-700 cursor-pointer">
                        {permission.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            {selectedPermissions.length === 0 && (
              <p className="text-xs text-red-500 mt-2">Sélectionnez au moins une permission</p>
            )}
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-200 font-medium"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-gradient-to-r from-[#5e17eb] to-[#7c3aed] text-white rounded-xl hover:from-[#7c3aed] hover:to-[#5e17eb] transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              disabled={loading || !roleName.trim() || selectedPermissions.length === 0}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleModal;